import React from "react";
import { ArrowRight, MapPin } from "lucide-react";
import { useLang } from "../contexts/LanguageContext";

const HERO_BG =
  "/images/hero.jpg";

const Hero = () => {
  const { t } = useLang();
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="top"
      className="relative min-h-[100vh] flex items-center overflow-hidden grain"
      data-testid="hero-section"
    >

      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${HERO_BG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(10,10,10,0.6) 0%, rgba(10,10,10,0.75) 50%, rgba(10,10,10,1) 100%)",
        }}
      />

      <div
        className="absolute -top-20 -right-20 w-[600px] h-[600px] rounded-full opacity-40 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(212,175,55,0.4) 0%, transparent 60%)",
          filter: "blur(60px)",
        }}
      />

      <div className="container-hd relative z-10 pt-36 md:pt-40 pb-20">
        <div className="max-w-4xl">

          <div
            className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full border border-[color:var(--hd-gold)]/40 bg-black/40 backdrop-blur-md mb-8 reveal"
            data-testid="hero-badge"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--hd-gold)] animate-pulse" />
            <span className="font-body text-[11px] md:text-xs text-white/90 uppercase tracking-[0.25em] font-medium">
              {t.hero.tagline}
            </span>
          </div>

          <h1
            className="font-display text-[clamp(1.5rem,7.2vw,6rem)] font-bold leading-[0.95] mb-8 reveal"
            style={{ animationDelay: "0.1s" }}
            data-testid="hero-title"
          >
            <span className="block text-white">{t.hero.title1}</span>
            <span className="block gold-gradient">{t.hero.title2}</span>
            <span className="block text-white italic font-serif font-normal text-[clamp(1.35rem,5.2vw,4.5rem)] mt-2">
              {t.hero.title3}
            </span>
            <span className="sr-only">
              Autoaufbereitung, Fahrzeugaufbereitung & Auto Detailing in Wien
            </span>
          </h1>

          <p
            className="text-base sm:text-lg text-white/70 max-w-2xl mb-10 reveal leading-relaxed"
            style={{ animationDelay: "0.2s" }}
            data-testid="hero-subtitle"
          >
            {t.hero.subtitle}
          </p>

          <div
            className="flex flex-wrap items-center gap-4 mb-16 reveal"
            style={{ animationDelay: "0.3s" }}
          >
            <button
              onClick={() => scrollTo("contact")}
              className="btn-gold"
              data-testid="hero-cta-primary"
            >
              {t.hero.ctaPrimary}
              <ArrowRight size={16} />
            </button>
            <button
              onClick={() => scrollTo("services")}
              className="btn-outline"
              data-testid="hero-cta-secondary"
            >
              {t.hero.ctaSecondary}
            </button>

            <div className="flex items-center gap-2 text-white/60 text-sm ml-2">
              <MapPin size={14} className="text-[color:var(--hd-gold)]" />
              <span className="font-body">Wien, Österreich</span>
            </div>
          </div>

          <div
            className="grid grid-cols-3 gap-3 sm:gap-6 md:gap-10 max-w-2xl reveal"
            style={{ animationDelay: "0.4s" }}
            data-testid="hero-stats"
          >
            {t.hero.stats.map((s, i) => (
              <div key={i} className="border-l border-[color:var(--hd-gold)]/30 pl-3 sm:pl-4">
                <div className="font-display text-2xl sm:text-3xl md:text-4xl font-bold gold-gradient">
                  {s.value}
                </div>
                <div className="text-white/50 text-[10px] sm:text-xs md:text-sm mt-1 uppercase tracking-wider leading-tight">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
